import type { TFunction } from 'i18next';
import { z } from 'zod';

function createEmailSchema(t: TFunction) {
  return z
    .string()
    .min(1, t('auth.validation.emailRequired'))
    .email(t('auth.validation.emailInvalid'));
}

function createPasswordSchema(t: TFunction) {
  return z
    .string()
    .min(8, t('auth.validation.passwordMin'))
    .regex(/[A-Z]/, t('auth.validation.passwordUpper'))
    .regex(/[a-z]/, t('auth.validation.passwordLower'))
    .regex(/[0-9]/, t('auth.validation.passwordNumber'));
}

export function createLoginSchema(t: TFunction) {
  return z.object({
    email: createEmailSchema(t),
    password: z.string().min(1, t('auth.validation.passwordRequired')),
  });
}
export type LoginFormData = z.infer<ReturnType<typeof createLoginSchema>>;

export function createRegisterSchema(t: TFunction) {
  return z
    .object({
      name: z
        .string()
        .min(1, t('auth.validation.nameRequired'))
        .min(2, t('auth.validation.nameMin'))
        .max(100, t('auth.validation.nameMax')),
      email: createEmailSchema(t),
      password: createPasswordSchema(t),
      confirmPassword: z.string().min(1, t('auth.validation.confirmPasswordRequired')),
      companyCode: z
        .string()
        .min(1, t('auth.validation.companyCodeRequired'))
        .min(4, t('auth.validation.companyCodeMin'))
        .max(20, t('auth.validation.companyCodeMax'))
        .regex(/^[A-Z0-9-]+$/, t('auth.validation.companyCodeFormat')),
      acceptedTerms: z.boolean().refine((val) => val === true, {
        message: t('auth.validation.acceptTerms'),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.validation.passwordsMismatch'),
      path: ['confirmPassword'],
    });
}
export type RegisterFormData = z.infer<ReturnType<typeof createRegisterSchema>>;

/**
 * Schema de cambio de contraseña desacoplado de i18n: recibe los mensajes ya
 * resueltos para poder usarse en áreas con distinta estrategia de traducción
 * (alumno = i18n; empresa/backoffice = textos fijos). Las reglas de la nueva
 * contraseña coinciden con createPasswordSchema.
 */
export interface ChangePasswordMessages {
  currentRequired: string;
  newMin: string;
  newUpper: string;
  newLower: string;
  newNumber: string;
  confirmRequired: string;
  mismatch: string;
  sameAsCurrent: string;
}

export function createChangePasswordSchema(m: ChangePasswordMessages) {
  return z
    .object({
      currentPassword: z.string().min(1, m.currentRequired),
      newPassword: z
        .string()
        .min(8, m.newMin)
        .regex(/[A-Z]/, m.newUpper)
        .regex(/[a-z]/, m.newLower)
        .regex(/[0-9]/, m.newNumber),
      confirmPassword: z.string().min(1, m.confirmRequired),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: m.mismatch,
      path: ['confirmPassword'],
    })
    .refine((data) => data.newPassword !== data.currentPassword, {
      message: m.sameAsCurrent,
      path: ['newPassword'],
    });
}
export type ChangePasswordFormData = z.infer<ReturnType<typeof createChangePasswordSchema>>;

/**
 * Schema de cambio de email desacoplado de i18n (recibe los mensajes ya
 * resueltos), para usarse en áreas con distinta estrategia de traducción.
 */
export interface ChangeEmailMessages {
  emailRequired: string;
  emailInvalid: string;
}

export function createChangeEmailSchema(m: ChangeEmailMessages) {
  return z.object({
    newEmail: z.string().min(1, m.emailRequired).email(m.emailInvalid),
  });
}
export type ChangeEmailFormData = z.infer<ReturnType<typeof createChangeEmailSchema>>;

export function createForgotPasswordSchema(t: TFunction) {
  return z.object({
    email: createEmailSchema(t),
  });
}
export type ForgotPasswordFormData = z.infer<ReturnType<typeof createForgotPasswordSchema>>;

export function createResetPasswordSchema(t: TFunction) {
  return z
    .object({
      password: createPasswordSchema(t),
      confirmPassword: z.string().min(1, t('auth.validation.confirmPasswordRequired')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.validation.passwordsMismatch'),
      path: ['confirmPassword'],
    });
}
export type ResetPasswordFormData = z.infer<ReturnType<typeof createResetPasswordSchema>>;

export function createAcceptInvitationSchema(t: TFunction) {
  return z
    .object({
      name: z
        .string()
        .min(1, t('auth.validation.nameRequired'))
        .min(2, t('auth.validation.nameMin'))
        .max(100, t('auth.validation.nameMax')),
      password: createPasswordSchema(t),
      confirmPassword: z.string().min(1, t('auth.validation.confirmPasswordRequired')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('auth.validation.passwordsMismatch'),
      path: ['confirmPassword'],
    });
}
export type AcceptInvitationFormData = z.infer<ReturnType<typeof createAcceptInvitationSchema>>;
